import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';
import { useTheme, alpha } from '@mui/material/styles';

/** One distinct value of a column, as offered in the filter checklist. */
export interface GridFilterValueOption {
  /** Normalized stored value ('' represents blank/null). */
  value: string;
  /** Human-readable label ('(Blanks)' for '', optionItems label when mapped). */
  label: string;
  /** How many rows (passing the OTHER columns' filters) carry this value. */
  count: number;
}

interface GridColumnFilterProps {
  headerName: string;
  values: GridFilterValueOption[];
  /** Currently included values; null = no filter on this column (all pass). */
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
}

/**
 * Excel-AutoFilter-style column filter (BUG-2026-0143): a funnel icon in the
 * column header opens a popover with a searchable checklist of the column's
 * distinct values. Toggles apply immediately; when every value is re-checked
 * the filter normalizes back to null so the icon returns to its idle state.
 */
export const GridColumnFilter = ({ headerName, values, selected, onChange }: GridColumnFilterProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const isActive = selected !== null;

  const visibleValues = useMemo(() => {
    if (!search.trim()) return values;
    const needle = search.trim().toLowerCase();
    return values.filter((v) => v.label.toLowerCase().includes(needle));
  }, [values, search]);

  /** Effective included set — null means "everything". */
  const effective = useMemo(
    () => selected ?? new Set(values.map((v) => v.value)),
    [selected, values],
  );

  const commit = (next: Set<string>) => {
    onChange(next.size >= values.length ? null : next);
  };

  const toggleValue = (value: string) => {
    const next = new Set(effective);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    commit(next);
  };

  // Select-all operates on the search-visible values (Excel behavior): with no
  // search it toggles the whole column, with a search it batch-toggles matches.
  const allVisibleChecked = visibleValues.length > 0 && visibleValues.every((v) => effective.has(v.value));
  const someVisibleChecked = visibleValues.some((v) => effective.has(v.value));
  const toggleAllVisible = () => {
    const next = new Set(effective);
    if (allVisibleChecked) visibleValues.forEach((v) => next.delete(v.value));
    else visibleValues.forEach((v) => next.add(v.value));
    commit(next);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Filter ${headerName}`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          p: 0.25,
          ml: 0.25,
          color: isActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.35),
          '&:hover': { color: isActive ? theme.palette.primary.dark : alpha(theme.palette.text.primary, 0.7) },
        }}
      >
        {isActive
          ? <FilterAltIcon sx={{ fontSize: 15 }} />
          : <FilterAltOutlinedIcon sx={{ fontSize: 15 }} />}
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: theme.spacing(33), maxWidth: theme.spacing(60), p: 1 } } }}
        onClick={(e) => e.stopPropagation()}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Search values..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 0.5, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
        />
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={allVisibleChecked}
            indeterminate={!allVisibleChecked && someVisibleChecked}
            disabled={visibleValues.length === 0}
            onChange={toggleAllVisible}
          />
          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, flex: 1 }}>
            Select all{search.trim() ? ' (matching)' : ''}
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 280, overflowY: 'auto', mt: 0.25 }}>
          {visibleValues.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', py: 2, color: 'text.secondary' }}>
              No matching values
            </Typography>
          ) : (
            visibleValues.map((v) => (
              <Box
                key={v.value}
                onClick={() => toggleValue(v.value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.125,
                  cursor: 'pointer', borderRadius: '4px',
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
                }}
              >
                <Checkbox size="small" sx={{ p: 0.5 }} checked={effective.has(v.value)} tabIndex={-1} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem', flex: 1, overflowWrap: 'anywhere',
                    ...(v.value === '' && { fontStyle: 'italic', color: 'text.secondary' }),
                  }}
                >
                  {v.label}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  {v.count}
                </Typography>
              </Box>
            ))
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
          <Button
            size="small"
            disabled={!isActive}
            onClick={() => {
              onChange(null);
              handleClose();
            }}
          >
            Clear filter
          </Button>
        </Box>
      </Popover>
    </>
  );
};
